import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import TopBar from '../components/TopBar';
import Logo from '../components/Logo';
import WifiSteps from '../components/WifiSteps';

// ── Validation (mirrors cloud-side, used to skip auto-claim on
//    obviously-bad URL params so the user lands on the editable
//    form with a useful error message instead of a server round-trip) ──

const DEVICE_ID_RE   = /^WT-\d{4,5}$/;
const CLAIM_TOKEN_RE = /^[A-Z0-9]{11}$/;

function normalizeToken(t) {
  return String(t || '').replace(/[-\s]/g, '').toUpperCase();
}

function formatTokenForDisplay(token) {
  const t = normalizeToken(token);
  if (t.length !== 11) return t;
  return `${t.slice(0, 4)}-${t.slice(4, 8)}-${t.slice(8)}`;
}

// Map cloud HttpsError codes to user-facing messages. The cloud
// function returns reasonable .message strings too — we prefer
// ours for the common cases so the UX is consistent regardless
// of which code path produced the error.
function messageForError(err) {
  const code = err?.code || '';
  const msg  = err?.message || '';
  if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
    return 'Please sign in to claim a device.';
  }
  if (code === 'functions/invalid-argument' || code === 'invalid-argument') {
    return msg || 'Please check the device ID, claim code, and location name.';
  }
  if (code === 'functions/not-found' || code === 'not-found') {
    return "We don't recognize that device ID. Check the code on your unit's label or the card from the box.";
  }
  if (code === 'functions/permission-denied' || code === 'permission-denied') {
    return "Claim code doesn't match. Check the card from your box.";
  }
  if (code === 'functions/failed-precondition' || code === 'failed-precondition') {
    // Cloud distinguishes "already claimed" vs "not seen yet" by
    // the message text; pass through the cloud's message because
    // it's the most specific source of truth.
    return msg || 'This device cannot be claimed right now. Try again in a minute.';
  }
  return 'Something went wrong. Try again, email support@sokowatersystems.com, '
    + 'or call or text (808) 747-4089.';
}

export default function ClaimDevice({ user }) {
  const navigate     = useNavigate();
  const location     = useLocation();
  const [searchParams] = useSearchParams();
  const urlDeviceId  = (searchParams.get('d') || '').trim().toUpperCase();
  const urlToken     = normalizeToken(searchParams.get('t'));
  const hasDeeplink  = !!urlDeviceId && !!urlToken;

  // Form state — pre-filled from URL params when present.
  const [deviceId, setDeviceId]         = useState(urlDeviceId);
  const [claimToken, setClaimToken]     = useState(formatTokenForDisplay(urlToken));
  const [locationName, setLocationName] = useState('');

  // Flow state.
  //   'idle'       — empty form (no deeplink) waiting for user input
  //   'autoClaim'  — deeplink present, auto-claim in progress
  //   'submitting' — user clicked the button manually
  //   'waiting'    — claim succeeded, polling for first reading
  //   'done'       — first reading arrived; about to navigate away
  const [phase, setPhase]   = useState(hasDeeplink ? 'autoClaim' : 'idle');
  const [error, setError]   = useState('');
  // Set when the claim fails specifically because the device hasn't reported to
  // the cloud yet (not online). Drives a "get it on wifi" help panel + retry,
  // instead of the plain error banner.
  const [notConnected, setNotConnected] = useState(false);
  const autoTriedRef        = useRef(false);  // one-shot guard for auto-claim
  const waitTimeoutRef      = useRef(null);

  // Redirect to login (with ?next=) if not signed in. The auth
  // wrapper in App.js does this for /claim and /setup too via
  // PrivateRoute, but defensive: if we render at all without a
  // user, send them through login with the deeplink preserved.
  useEffect(() => {
    if (!user) {
      const fullPath = location.pathname + location.search;
      navigate(`/login?next=${encodeURIComponent(fullPath)}`, { replace: true });
    }
  }, [user, location, navigate]);

  // ── Auto-claim on mount when URL has both ?d and ?t ──
  // Runs at most once per page load (autoTriedRef guard) so reload
  // after a successful claim doesn't re-fire the attempt.
  useEffect(() => {
    if (!user) return;
    if (phase !== 'autoClaim') return;
    if (autoTriedRef.current) return;
    autoTriedRef.current = true;

    // Quick local validation — if the URL is obviously malformed,
    // skip the network call and drop straight into the editable
    // form with a helpful error.
    if (!DEVICE_ID_RE.test(urlDeviceId) || !CLAIM_TOKEN_RE.test(urlToken)) {
      setError("The link looks incomplete. Check the device ID and claim code from the card in your box.");
      setPhase('idle');
      return;
    }

    // Auto-claim still needs a location name — that's the one
    // thing we can't pre-fill from the QR. Bounce into the
    // editable form (no error) and let the user type it.
    if (!locationName.trim()) {
      setPhase('idle');
      return;
    }

    runClaim(urlDeviceId, urlToken, locationName.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, phase]);

  async function runClaim(devId, token, locName) {
    setError('');
    setNotConnected(false);
    setPhase(phase === 'idle' ? 'submitting' : 'autoClaim');
    try {
      const claimDevice = httpsCallable(functions, 'claimDevice');
      await claimDevice({
        deviceId:     devId,
        claimToken:   token,
        locationName: locName,
      });
      setPhase('waiting');
      startWaitingForReading(devId);
    } catch (err) {
      console.error('claimDevice failed:', err);
      // The device exists but hasn't reported to the cloud yet → it's not online.
      // Show the wifi-connect help + retry rather than a terse error. (Branch on
      // the structured reason; fall back to the message text for older backends.)
      const offline = err?.details?.reason === 'not-connected'
        || /connected to the cloud/i.test(err?.message || '');
      if (offline) {
        setNotConnected(true);
        setError('');
      } else {
        setError(messageForError(err));
      }
      // Auto-claim failed → drop into editable form so the user
      // can correct and retry. Manual submission failure stays on
      // the form (already there).
      setPhase('idle');
    }
  }

  // Retry the claim with the values already in the form (used by the
  // "get it online" panel after the user connects the device to wifi).
  function retryClaim() {
    runClaim(deviceId.trim().toUpperCase(), normalizeToken(claimToken), locationName.trim());
  }

  // ── Wait for first reading after claim ─────────────────────
  // We watch the device doc for an updated lastReading.timestamp.
  // The device was already pushing (lastSeen ≤ 24h was a claim
  // precondition), so a fresh reading should arrive within 30s.
  // If not, after 60s we still let the user proceed — the device
  // is claimed regardless; calibration just won't have a live
  // reading on screen yet.
  function startWaitingForReading(devId) {
    const devRef = doc(db, 'devices', devId);
    let satisfied = false;

    const unsub = onSnapshot(devRef, async (snap) => {
      if (satisfied) return;
      if (!snap.exists()) return;
      const data = snap.data();
      const ts = data.lastReading?.timestamp;
      // After the claim, the device's next push (within ~5s in
      // fast mode) updates lastReading. Any reading we see now
      // counts as "device is here and reporting."
      if (ts) {
        satisfied = true;
        clearTimeout(waitTimeoutRef.current);
        unsub();
        await proceedAfterClaim(devId);
      }
    }, (err) => {
      console.error('waiting-for-reading snapshot error:', err);
    });

    waitTimeoutRef.current = setTimeout(async () => {
      if (satisfied) return;
      satisfied = true;
      unsub();
      // Soft-timeout. Claim succeeded; just move forward.
      await proceedAfterClaim(devId);
    }, 60_000);
  }

  // Route to calibration wizard if the device still needs onboarding
  // setup, otherwise to dashboard. We re-read the device doc to get
  // the freshest tank1 state. A device is "set up" only when the
  // sensor is calibrated AND the tank levels are entered — a
  // factory-calibrated unit has calPoints but no levels yet, so it
  // still routes to setup (the wizard skips to the levels step).
  async function proceedAfterClaim(devId) {
    setPhase('done');
    try {
      const snap = await getDoc(doc(db, 'devices', devId));
      const data = snap.exists() ? snap.data() : {};
      const t1 = data.tank1 || {};
      const hasCal = Array.isArray(t1.calPoints) && t1.calPoints.length >= 2;
      const hasTankInfo = typeof t1.emptyFt === 'number' && typeof t1.fullFt === 'number';
      const needsSetup = !hasCal || !hasTankInfo;
      // Set up first if needed; the wizard then goes on to /plus. A monitor
      // that's already set up goes straight to activating its included year
      // of Plus (that page sends anyone not eligible on to the dashboard).
      navigate(needsSetup ? `/setup/${devId}` : `/plus/${devId}`, {
        replace: true,
        state: needsSetup ? { onboarding: true } : undefined,
      });
    } catch (err) {
      console.error('proceedAfterClaim read failed:', err);
      navigate('/dashboard', { replace: true });
    }
  }

  // ── Manual submit (from the editable form) ─────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const devId  = deviceId.trim().toUpperCase();
    const token  = normalizeToken(claimToken);
    const locName = locationName.trim();

    if (!DEVICE_ID_RE.test(devId)) {
      setError('Device ID should look like WT-0042.');
      return;
    }
    if (!CLAIM_TOKEN_RE.test(token)) {
      setError('Claim code should be 11 characters (letters and numbers).');
      return;
    }
    if (!locName) {
      setError('Please enter a location name.');
      return;
    }
    if (locName.length > 32) {
      setError('Location name must be under 32 characters.');
      return;
    }
    runClaim(devId, token, locName);
  }

  // Cleanup any pending wait timer on unmount.
  useEffect(() => () => {
    if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
  }, []);

  // While auth check is in flight (user undefined briefly), render
  // a minimal placeholder. The useEffect above will redirect.
  if (!user) {
    return (
      <div className="loading-page">
        <Logo variant="icon" width={48} />
        <p>Loading…</p>
      </div>
    );
  }

  // ── Render: auto-claim spinner ─────────────────────────────
  if (phase === 'autoClaim') {
    return (
      <div className="app-container">
        <TopBar subtitle="Connecting your device" />
        <div className="page-content">
          <div className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }} />
            <h3>Connecting {deviceId}…</h3>
            <p style={{ color: 'var(--muted, #6b7785)', marginTop: 8 }}>
              This should only take a moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: post-claim, waiting for first reading ──────────
  if (phase === 'waiting' || phase === 'done') {
    return (
      <div className="app-container">
        <TopBar subtitle="Almost there" />
        <div className="page-content">
          <div className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }} />
            <h3>Waiting for your device to report…</h3>
            <p style={{ color: 'var(--muted, #6b7785)', marginTop: 8 }}>
              Your tank monitor is connected. Just confirming it can send readings — usually a few seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: editable form (idle, submitting, or post-error) ─
  const submitting = phase === 'submitting';

  return (
    <div className="app-container">
      <TopBar subtitle={hasDeeplink ? 'Connect your device' : 'Set up a device'} />
      <div className="page-content">

        <div className="card add-device-form">
          <div className="add-device-form__header">
            <h3>{hasDeeplink ? 'Confirm and connect' : 'Connect a device'}</h3>
          </div>

          <p style={{ marginTop: 0, color: 'var(--muted, #6b7785)' }}>
            {hasDeeplink
              ? "We've filled in your device ID and claim code from the link. Add a location name and tap Connect."
              : "Type the device ID and claim code from your unit's quick-start card."}
          </p>

          {error && (
            <div className="alert-banner fault">
              <div className="alert-banner-desc">{error}</div>
            </div>
          )}

          {notConnected && (
            <div className="claim-offline">
              <div className="claim-offline__title">Get your device online</div>
              <p className="claim-offline__lede">
                We found your device, but it hasn't reported to the cloud yet — it
                needs to be powered on and connected to wifi. Then tap Try again.
              </p>
              <WifiSteps compact />
              <div className="claim-offline__actions">
                <button type="button" className="btn btn-primary" onClick={retryClaim}>
                  Try again
                </button>
                <Link to="/install" className="btn btn-secondary">Install instructions</Link>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="field">
              <label>Device ID</label>
              <input
                type="text"
                placeholder="e.g. WT-0042"
                value={deviceId}
                onChange={e => setDeviceId(e.target.value)}
                autoCapitalize="characters"
                required
              />
            </div>

            <div className="field">
              <label>Claim code</label>
              <input
                type="text"
                placeholder="e.g. K7M3-PQR9-X2N"
                value={claimToken}
                onChange={e => setClaimToken(e.target.value)}
                autoCapitalize="characters"
                required
              />
              <div className="hint">
                Found on the quick-start card from your box, or printed below the QR code.
              </div>
            </div>

            <div className="field">
              <label>Location name</label>
              <input
                type="text"
                placeholder="Home"
                value={locationName}
                onChange={e => setLocationName(e.target.value)}
                maxLength={32}
                required
                autoFocus={hasDeeplink}
              />
              <div className="hint">
                A name you'll recognize. You can change it later.
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Connecting…' : 'Connect device'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
